import { fabric } from "fabric"
import { type DrawableMixin } from "./objects/drawable_mixin"
import GroupObject from "./objects/group"
import WithImages from "./with_images"
import Calc from "@/common/util/calculator"
import EllipseObject from "@/signomatic/app/objects/ellipse"
import ImageObject from "@/signomatic/app/objects/image"
import PathObject from "@/signomatic/app/objects/path"
import PatternObject from "@/signomatic/app/objects/pattern"
import RectObject from "@/signomatic/app/objects/rect"
import type SignDrawables from "@/signomatic/app/sign_drawables"
import { log } from "@/signomatic/util/logger"
import { parisBorderPath, stockholmBorderPaths } from "@/signomatic/util/shapes"

interface OverlayInfo {
  path: string
  width?: number
  height?: number
}

interface OverlayMap {
  [index: string]: OverlayInfo
}

interface PatternCache {
  imageInfo: OverlayInfo
  scale: number
  pattern: PatternObject
}

const overlayPaths: OverlayMap = {
  banner: { path: "/images/signomatic/backgrounds/banner.png" },
  brushed: { path: "/images/signomatic/backgrounds/brushed.png" },
  glossy: { path: "/images/signomatic/backgrounds/glossy.png" },
  transparent: { path: "/images/signomatic/backgrounds/transparent.png" },
  wood: { path: "/images/signomatic/backgrounds/wood_mdf.png" },
  stockholm: { path: "/images/signomatic/backgrounds/shiny_stockholm.png" },
  rome: { path: "/images/signomatic/backgrounds/shiny_rome.png" },
  paris: { path: "/images/signomatic/backgrounds/shiny_paris.png" },
  london: { path: "/images/signomatic/backgrounds/shiny_london.png" },
  berlin: { path: "/images/signomatic/backgrounds/shiny_berlin.png" },
  frosty: { path: "/images/signomatic/backgrounds/frosty.png" },
  badgeRoundedRectangle: { path: "/images/signomatic/backgrounds/badge_rounded_rectangle.png" },
  acrylReflection: { path: "/images/signomatic/backgrounds/acryl_reflection.png" },
  reflective: {
    path: "/images/signomatic/backgrounds/reflective.png",
    width: 300,
    height: 300,
  },
}

export class SignImageOverlay extends WithImages {
  private readonly drawables: SignDrawables
  private readonly paths: string[]

  constructor(drawables: SignDrawables, paths: string[]) {
    super()
    this.drawables = drawables
    this.paths = paths
    this.loadImages()
  }

  drawable(): DrawableMixin | null {
    if (this.imagesLoaded) {
      const images = []
      const topExtra = this.drawables.externalTop ? this.drawables.externalTopPadding * 2 : 0
      const sideExtra = this.drawables.externalSides ? this.drawables.externalLeftPadding * 2 : 0

      for (const path of this.paths) {
        const source = this.image(path)

        if (source) {
          images.push(
            new ImageObject(source, {
              scaleX: ((this.drawables.state.widthPx || 0) + sideExtra) / (source.naturalWidth || source.width),
              scaleY: ((this.drawables.state.heightPx || 0) + topExtra) / (source.naturalHeight || source.height),
            }).addClass("overlay")
          )
        }
      }

      return new GroupObject(images)
    }

    return null
  }

  protected redraw() {
    this.drawables.redraw()
  }

  protected imagePaths() {
    return this.paths
  }
}

export class SignImagePatternOverlay extends WithImages {
  private readonly drawables: SignDrawables
  private readonly imageInfo: OverlayInfo
  private patternCache: PatternCache | null = null

  constructor(drawables: SignDrawables, imageInfo: OverlayInfo) {
    super()
    this.drawables = drawables
    this.imageInfo = imageInfo
    this.loadImages()
  }

  drawable(): DrawableMixin | null {
    if (this.imagesLoaded && this.image()) {
      const fill = this.cachePattern(this.imageInfo, this.drawables.defaultScale)
      let base = this.drawables.cutlineDrawable(true)

      if (fill) {
        if (base instanceof fabric.Group) {
          base = base.getObjects()[0] as DrawableMixin
        }

        base
          .set({
            left: 0,
            top: 0,
            fill,
            stroke: "transparent",
            shadow: undefined,
          })
          .clearClasses()
          .addClass("preview", "overlay")

        return base
      }
    }

    return null
  }

  protected redraw() {
    this.drawables.redraw()
  }

  protected imagePaths() {
    return this.imageInfo.path
  }

  private cachePattern(imageInfo: OverlayInfo, scale: number): PatternObject | null {
    const cache = this.patternCache

    if (cache && cache.imageInfo === imageInfo && cache.scale === scale) {
      log("pattern cache hit")
      return cache.pattern
    }

    const source = this.image()

    if (!source) {
      return null
    }

    const fill = new PatternObject({
      source,
      repeat: "repeat",
      offsetX: 0,
      offsetY: 0,
    })

    fill.id = Math.round(Math.random() * 99999999)
    fill.svgUrl = imageInfo.path
    fill.svgScale = 10 / fabric.devicePixelRatio

    this.patternCache = {
      imageInfo,
      scale,
      pattern: fill,
    }

    log("pattern cache miss")

    return fill
  }
}

const brassMap: OverlayMap = {
  stockholm: overlayPaths.stockholm,
  rome: overlayPaths.rome,
  london: overlayPaths.london,
  berlin: overlayPaths.berlin,
  paris: overlayPaths.paris,
  prague: overlayPaths.paris,
  dublin: overlayPaths.paris,
  lisbon: overlayPaths.berlin,
  copenhagen: overlayPaths.berlin,
  athens: overlayPaths.berlin,
  amsterdam: overlayPaths.berlin,
  tallinn: overlayPaths.berlin,
}

interface BorderMap {
  [index: string]: (base?: fabric.Object) => fabric.Object
}

export default class SignOverlay {
  brassBorderMap: BorderMap = {
    paris: this.parisOverlay,
    stockholm: this.stockholmOverlay,
    london: this.brassBevelOverlay,
    oslo: this.brassBevelOverlay,
    berlin: this.brassBevelOverlay,
    rome: this.brassBevelOverlay,
    sydney: this.brassBevelOverlay,
    dublin: this.brassBevelOverlay,
    lisbon: this.brassBevelOverlay,
    copenhagen: this.brassBevelOverlay,
    athens: this.brassBevelOverlay,
    amsterdam: this.brassBevelOverlay,
    tallinn: this.brassBevelOverlay,
  }

  private readonly drawables: SignDrawables

  constructor(drawables: SignDrawables) {
    this.drawables = drawables
  }

  createBaseOverlay(state: SignState) {
    const { color, badgeName, material, shape } = state
    let image

    if (color === "#daa520" || color === "#bbbbbb" || color === "#c27268") {
      image = overlayPaths.transparent
    }

    if (material === "wood") {
      image = overlayPaths.wood
    }

    switch (this.drawables.signType) {
      case "banner":
        image = overlayPaths.banner
        break
      case "acryl_sign":
        if (color === "#f0f0f0") {
          image = material === "frosty" ? overlayPaths.frosty : overlayPaths.glossy
        }
        break
      case "acryl_alu_sign":
        image = overlayPaths.acrylReflection
        break
      case "aluminium_sign":
      case "double_sided_sign":
        if (material === "reflective") {
          image = overlayPaths.reflective
        }
        if (material === "aluminium") {
          image = overlayPaths.brushed
        }
        break
      case "color_badge":
        if (["#ffe472", "#e3e0db"].includes(color)) {
          image = overlayPaths.brushed
        }
        break
      case "brass_sign":
        image =
          ["#d8c98d", "#c8c6cd"].includes(color) ||
          (["berlin", "copenhagen", "athens", "amsterdam", "tallinn"].includes(badgeName || "") && color === "#ffe472")
            ? (image = brassMap[badgeName || ""])
            : (image = overlayPaths.brushed)
        break
      case "label_plates":
        image =
          badgeName ||
          ["stainless", "aluminium"].includes(material) ||
          (material === "aluminium_lacquered" && color === "#eeeeee")
            ? overlayPaths.brushed
            : image
        break
      case "stainless_steel_sign":
      case "etched_aluminium_sign":
        image = overlayPaths.brushed
        break
    }

    if (this.drawables.signType === "color_badge" && shape === "bulge") {
      const images = image ? [image.path] : []

      if (["brass", "nickel", "aluminium"].includes(material)) {
        images.push(overlayPaths.badgeRoundedRectangle.path)
      }

      return new SignImageOverlay(this.drawables, images)
    }

    if (image) {
      return image.width
        ? new SignImagePatternOverlay(this.drawables, image)
        : new SignImageOverlay(this.drawables, [image.path])
    }

    return null
  }

  topDrawable(engraving = false): DrawableMixin | null {
    const { border, badgeName } = this.drawables.state
    let overlay: DrawableMixin | null = null
    const base = this.drawables.formDrawable()

    if (border === "rounded") {
      overlay = this.roundedBorderOverlay(base).addClass("border")
    } else if (border === "retro") {
      overlay = (engraving ? this.retroBorderEngraving() : this.retroBorderOverlay()).addClass("border")
    }

    if (this.drawables.signType === "brass_sign") {
      const borderOverlay = this.brassBorderMap[badgeName || ""]

      if (borderOverlay) {
        overlay = borderOverlay.bind(this)(base)
      }
    }

    if (overlay) {
      if (overlay instanceof fabric.Group) {
        for (const obj of overlay.getObjects()) {
          const drawable = obj as DrawableMixin
          if (drawable.addClass) {
            drawable.addClass("overlay")
          }
        }
      } else {
        overlay.addClass("overlay")
      }
    }

    return overlay
  }

  offsetOverlay(state: SignState) {
    const { color } = state
    let image

    if (this.drawables.signType == "acryl_alu_sign" && color == "#eeeeee") {
      image = overlayPaths.brushed
    }

    if (image) {
      return new SignImageOverlay(this.drawables, [image.path])
    }

    return null
  }
  private roundedBorderOverlay(base: fabric.Object) {
    const { widthPx, heightPx, borderColor } = this.drawables.state
    const minSide = Math.min(widthPx, heightPx)
    const width = Math.max(minSide * 0.042, Calc.mmToPx(2.5))
    const radius = minSide * 0.1

    const obj = base.toObject()
    const overlay = new RectObject(obj)

    return overlay.set({
      strokeWidth: width,
      stroke: borderColor,
      shadow: undefined,
      fill: "transparent",
      width: widthPx - width,
      height: heightPx - width,
      rx: radius,
      ry: radius,
    })
  }

  private parisOverlay() {
    const { widthPx, color } = this.drawables.state
    const black = color === "#252526"
    const fill = black ? "#000000" : "#7A724E"
    const opacity = black ? 0.4 : 0.5
    return new PathObject(parisBorderPath(widthPx), {
      fill,
      opacity,
    })
  }

  private stockholmOverlay() {
    const { widthPx, heightPx, color } = this.drawables.state
    const dark = color === "#e3e0db"
    const fill = dark ? "#000000" : "#7A724E"
    const opacity = dark ? 0.4 : 0.5

    const items = stockholmBorderPaths(widthPx, heightPx).map(path =>
      new PathObject(path, { fill, opacity }).addClass("overlay")
    )

    return new GroupObject(items, {
      left: 0,
      top: 0,
      width: widthPx,
      height: heightPx,
    })
  }

  private brassBevelOverlay(base: fabric.Object) {
    const { badgeName } = this.drawables.state
    const [bevelW, bevelH] = this.getBrassBevelWidth()
    const obj = base.toObject()

    let overlay = null
    const params = {
      strokeWidth: bevelH,
      stroke: "#666666",
      fill: "transparent",
      width: (base.width || 0) - bevelW,
      height: (base.height || 0) - bevelH,
      opacity: 0.3,
    }

    overlay = ["london", "oslo", "dublin"].includes(badgeName || "")
      ? new EllipseObject({
          ...obj,
          ...params,
          rx: ((base.width || 0) - bevelW) / 2,
          ry: ((base.height || 0) - bevelH) / 2,
        })
      : new RectObject({ ...obj, ...params })

    overlay.set("shadow", undefined)
    overlay.addClass("overlay", "bevel")

    return overlay
  }

  private getBrassBevelWidth() {
    const { badgeName, material, color } = this.drawables.state

    if (this.drawables.signType === "brass_sign") {
      if (badgeName === "london") {
        return [Calc.mmToPx(2.5), Calc.mmToPx(2.5)]
      } else if (badgeName === "oslo") {
        return [Calc.mmToPx(2.7), Calc.mmToPx(2.7)]
      } else if (badgeName === "paris") {
        return [Calc.mmToPx(13.5), Calc.mmToPx(13.5)]
      } else if (badgeName === "stockholm") {
        return [material === "nickel" ? Calc.mmToPx(15) : Calc.mmToPx(14), Calc.mmToPx(5)]
      } else if (["berlin", "copenhagen", "athens", "amsterdam", "tallinn"].includes(badgeName || "")) {
        return [Calc.mmToPx(1.8), Calc.mmToPx(1.8)]
      } else if (badgeName === "rome") {
        if (material === "nickel" || (material === "brass" && color === "#ffe472")) {
          return [Calc.mmToPx(1.8), Calc.mmToPx(1.8)]
        } else {
          return [Calc.mmToPx(2.3), Calc.mmToPx(2.3)]
        }
      } else if (badgeName === "sydney") {
        return [Calc.mmToPx(1.5), Calc.mmToPx(1.5)]
      } else if (badgeName === "dublin") {
        return [Calc.mmToPx(6), Calc.mmToPx(6)]
      } else if (badgeName === "lisbon") {
        return [Calc.mmToPx(1.5), Calc.mmToPx(1.5)]
      }
    }

    return [0, 0]
  }

  private retroBorderOverlay() {
    const { widthPx, heightPx, borderColor } = this.drawables.state
    const params = this.drawables.retroParams()
    const width = widthPx - params.strokeWidth - params.distance * 2
    const height = heightPx - params.strokeWidth - params.distance * 2

    const overlay = new PathObject(this.retroPath(width, height, params.radius, params.strokeWidth))

    return overlay.set({
      strokeWidth: params.strokeWidth,
      stroke: borderColor,
      shadow: undefined,
      fill: "transparent",
    })
  }

  private retroBorderEngraving() {
    const params = this.drawables.retroParams()
    let { widthPx, heightPx } = this.drawables.state

    widthPx = widthPx - params.strokeWidth - params.distance * 2 + this.drawables.laserAdjustment
    heightPx = heightPx - params.strokeWidth - params.distance * 2 + this.drawables.laserAdjustment

    const path = new PathObject(
      this.retroPath(widthPx, heightPx, params.radius, params.strokeWidth, true, params.distance)
    )

    return path.set({
      strokeWidth: params.strokeWidth,
      stroke: "#000000",
      shadow: undefined,
      fill: "transparent",
    })
  }

  private retroPath(width: number, height: number, radius: number, stroke: number, outline = false, distance = 0) {
    const commands = []
    let x = -width / 2
    let y = height / 2

    if (outline) {
      x = distance + stroke / 2
      y = distance + stroke / 2 + height
    }

    commands.push(["M", x + radius, y])

    if (!outline) {
      commands.push(["L", x + width - radius + stroke / 2, y])
    }

    commands.push(["L", x + width - radius, y])
    commands.push(["S", x + width - radius, y - radius, x + width, y - radius])

    if (!outline) {
      commands.push(["L", x + width, y - radius + stroke / 2])
      commands.push(["L", x + width, y - height + radius - stroke / 2])
    }

    commands.push(["L", x + width, y - height + radius])
    commands.push(["S", x + width - radius, y - height + radius, x + width - radius, y - height])

    if (!outline) {
      commands.push(["L", x + width - radius + stroke / 2, y - height])
      commands.push(["L", x + radius - stroke / 2, y - height])
    }

    commands.push(["L", x + radius, y - height])
    commands.push(["S", x + radius, y - height + radius, x, y - height + radius])

    if (!outline) {
      commands.push(["L", x, y - height + radius - stroke / 2])
      commands.push(["L", x, y - radius + stroke / 2])
    }

    commands.push(["L", x, y - radius])
    commands.push(["S", x + radius, y - radius, x + radius, y])

    if (!outline) {
      commands.push(["L", x + radius - stroke / 2, y])
    }

    commands.push(["Z"])

    return commands
  }
}
